import { apiFetch } from '../../../lib/http';
import { postEventStream } from '../../../lib/sse';

export const coursesApi = {
  // -- Course Creation --
  // If `studentId` is provided, the request is routed through the parent portal
  // and the resulting course is owned by that student. Otherwise it's a regular
  // self-serve course creation for the authenticated user.
  createCourse(payload, { auth, studentId } = {}) {
    const path = studentId
      ? `/api/v1/parent/students/${encodeURIComponent(studentId)}/courses`
      : '/api/v1/courses/create';
    return apiFetch(path, {
      method: 'POST',
      auth,
      body: payload,
    });
  },

  /**
   * Stream course creation progress via SSE (stage/complete/error events).
   * See docs/sse-course-creation-api-contract.md. Same routing rule as
   * createCourse: a studentId routes through the parent portal so the
   * resulting course is owned by that student. Returns the async iterable
   * directly — the caller consumes it.
   */
  createCourseStream({ payload, studentId, signal }) {
    const path = studentId
      ? `/api/v1/parent/students/${encodeURIComponent(studentId)}/courses/stream`
      : '/api/v1/courses/create/stream';
    return postEventStream(path, payload, { signal });
  },

  // -- PDF Course Creation --
  /**
   * Upload a PDF file to create a course from its contents.
   * Returns a processingId that can be polled for status updates.
   *
   * @param {{
   *   file: File,
   *   title?: string,
   *   difficulty?: 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED',
   *   courseDomain?: string,
   *   expectedOutcomes?: string
   * }} params
   * @param {{ auth: boolean }} options
   * @returns {Promise<{ processingId: string, status: string, message: string, estimatedCompletionSeconds: number | null }>}
   */
  async createCourseFromPdf({ file, title, difficulty, courseDomain, expectedOutcomes }, { auth, studentId } = {}) {
    const formData = new FormData();
    formData.append('file', file);
    if (title) formData.append('title', title);
    if (difficulty) formData.append('difficulty', difficulty);
    if (courseDomain) formData.append('courseDomain', courseDomain);
    if (expectedOutcomes) formData.append('expectedOutcomes', expectedOutcomes);

    const path = studentId
      ? `/api/v1/parent/students/${encodeURIComponent(studentId)}/courses/from-pdf`
      : '/api/v1/courses/create-from-pdf';

    // Use apiFetch with rawResponse to handle FormData properly
    // apiFetch won't stringify FormData and will let the browser set the Content-Type
    return apiFetch(path, {
      method: 'POST',
      auth,
      body: formData,
      timeout: 120000, // 2 minute timeout for file uploads (large PDFs)
    });
  },

  /**
   * Stream PDF course-creation progress via SSE (stage/complete/error events).
   * See the PDF SSE section of docs/sse-course-creation-api-contract.md. The
   * `stage` events carry a `processingId` — save it so a dropped connection can
   * fall back to getPdfProcessingStatus polling without re-uploading the file.
   *
   * Self-serve only: there is no parent-scoped PDF stream endpoint, so parent
   * assignment stays on the non-streaming createCourseFromPdf + poll flow.
   * Returns the async iterable directly — the caller consumes it.
   *
   * @param {{
   *   file: File,
   *   title?: string,
   *   difficulty?: 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED',
   *   courseDomain?: string,
   *   expectedOutcomes?: string,
   *   signal?: AbortSignal
   * }} params
   */
  createCourseFromPdfStream({ file, title, difficulty, courseDomain, expectedOutcomes, signal }) {
    const formData = new FormData();
    formData.append('file', file);
    if (title) formData.append('title', title);
    if (difficulty) formData.append('difficulty', difficulty);
    if (courseDomain) formData.append('courseDomain', courseDomain);
    if (expectedOutcomes) formData.append('expectedOutcomes', expectedOutcomes);

    return postEventStream('/api/v1/courses/create-from-pdf/stream', formData, { signal });
  },

  /**
   * Poll for PDF processing status.
   *
   * @param {{ processingId: string }} params
   * @param {{ auth: boolean }} options
   * @returns {Promise<{
   *   processingId: string,
   *   status: 'QUEUED' | 'EXTRACTING_TEXT' | 'ANALYZING_CONTENT' | 'GENERATING_OUTLINE' | 'CREATING_COURSE' | 'COMPLETED' | 'FAILED',
   *   progress: number,
   *   message: string | null,
   *   courseId: string | null,
   *   firstLessonId: string | null,
   *   errorCode: string | null,
   *   errorMessage: string | null
   * }>}
   */
  // Parent-uploaded PDF jobs are owned by the student, not the parent caller.
  // The shared /courses/pdf-status/:id route filters by the caller's own uid
  // and 404s for the parent — must use the parent-scoped route instead. (The
  // old "universally scoped" comment here reflected an earlier backend
  // contract that's since changed.)
  getPdfProcessingStatus({ processingId }, { auth, studentId } = {}) {
    const path = studentId
      ? `/api/v1/parent/students/${encodeURIComponent(studentId)}/pdf-status/${encodeURIComponent(processingId)}`
      : `/api/v1/courses/pdf-status/${encodeURIComponent(processingId)}`;
    return apiFetch(path, {
      method: 'GET',
      auth,
    });
  },

  // -- Dashboard --
  fetchOverview({ userId, auth, limit, offset }) {
    const body = { userId };
    if (typeof limit === 'number') body.limit = limit;
    if (typeof offset === 'number') body.offset = offset;
    return apiFetch('/api/v1/dashboard/overview', {
      method: 'POST',
      auth,
      body,
    });
  },

  setBookmark({ courseId, auth }) {
    return apiFetch(`/api/v1/dashboard/bookmark`, {
      method: 'POST',
      auth,
      body: { courseId },
    });
  },

  deleteCourse({ courseId, userId, auth }) {
    return apiFetch('/api/v1/courses/delete', {
      method: 'POST',
      auth,
      body: { userId, courseId },
    });
  },

  // -- Lessons -- 
  fetchOutline({ userId, courseId, auth }) {
    return apiFetch('/api/v1/courses/outline', {
      method: 'POST',
      auth,
      body: { userId, courseId },
    });
  },

  fetchLesson({ userId, courseId, lessonId, auth }) {
    return apiFetch('/api/v1/lessons/view', {
      method: 'POST',
      auth,
      body: { userId, courseId, lessonId },
    });
  },

  /**
   * Fetch unified section view with all content types pre-loaded.
   * For QUIZ sections, includes sessionId and message history.
   * For KNOWLEDGE_CHECK sections, includes questions and previous attempts.
   *
   * @param {{ courseId: string, sectionId: string, auth: boolean }} params
   * @returns {Promise<import('../types/sectionView.types').SectionViewResponse>}
   */
  fetchSectionView({ courseId, sectionId, auth }) {
    return apiFetch('/api/v1/sections/view', {
      method: 'POST',
      auth,
      body: { courseId, sectionId },
    });
  },

  // -- Quizzes --

  /**
   * Initialize or resume a quiz session for a lesson.
   * Returns session with full message history, status, score, etc.
   */
  quizStart({ courseId, lessonId, auth }) {
    return apiFetch('/api/v1/quizzes/start', {
      method: 'POST',
      auth,
      body: { courseId, lessonId },
    });
  },

  /**
   * Submit a user answer and receive AI evaluation.
   */
  quizMessage({ sessionId, message, auth }) {
    return apiFetch('/api/v1/quizzes/message', {
      method: 'POST',
      auth,
      body: { sessionId, message },
    });
  },

  /**
   * Submit a user answer and stream the AI evaluation as SSE events
   * (stage/token/complete/error). See docs/sse-quiz-grading-api-contract.md.
   * Returns the async iterable directly — the caller consumes it.
   */
  quizMessageStream({ sessionId, message, signal }) {
    return postEventStream('/api/v1/quizzes/message/stream', { sessionId, message }, { signal });
  },

  // -- Knowledge Checks --

  /**
   * Fetch knowledge check questions for a lesson.
   * Does NOT include correct answers (to prevent cheating).
   */
  fetchKnowledgeCheck({ lessonId, auth }) {
    return apiFetch(`/api/v1/lessons/${lessonId}/knowledge-check`, {
      method: 'GET',
      auth,
    });
  },

  /**
   * Submit knowledge check answers and get results with explanations.
   */
  submitKnowledgeCheck({ lessonId, checkId, answers, auth }) {
    return apiFetch(`/api/v1/lessons/${lessonId}/knowledge-check/submit`, {
      method: 'POST',
      auth,
      body: { lessonId, checkId, answers },
    });
  },

  // -- User Insights --

  /**
   * Get learner insights for the authenticated user.
   */
  fetchInsights({ auth }) {
    return apiFetch('/api/v1/users/insights', {
      method: 'GET',
      auth,
    });
  },

  // -- Ask Graspd --

  /**
   * Ask Graspd for an explanation of selected text within a lesson.
   * @param {{
   *   courseId: string,
   *   lessonId: string,
   *   sectionId: string,
   *   selectedText: string,
   *   startOffset: number,
   *   endOffset: number,
   *   questionType: 'EXPLAIN' | 'EXAMPLE' | 'DEEPER' | 'WHY' | 'HOW' | 'CUSTOM',
   *   customQuestion?: string,
   *   auth: boolean
   * }} params
   * @returns {Promise<{ id: string, answer: string, questionType: string, selectedText: string, startOffset: number, endOffset: number, tokensUsed?: number, createdAt?: string }>}
   */
  askGraspd({ courseId, lessonId, sectionId, selectedText, startOffset, endOffset, questionType, customQuestion, auth }) {
    return apiFetch('/api/v1/ask-graspd', {
      method: 'POST',
      auth,
      body: { courseId, lessonId, sectionId, selectedText, startOffset, endOffset, questionType, customQuestion },
    });
  },

  /**
   * Fetch Ask Graspd history for a section.
   * @param {{ sectionId: string, auth: boolean }} params
   * @returns {Promise<{ sectionId: string, items: Array<{ id: string, selectedText: string, startOffset: number, endOffset: number, questionType: string, customQuestion?: string, answer: string, createdAt: string }> }>}
   */
  fetchAskGraspdHistory({ sectionId, auth }) {
    return apiFetch(`/api/v1/ask-graspd/history/${sectionId}`, {
      method: 'GET',
      auth,
    });
  },
};